import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, Trash2, Play, Download, Share2, Edit3, FolderOpen, Sparkles, ArrowLeft, LogIn, Star, Eye, ListMusic, Plus, X, Check, TrendingUp } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { API, useAuth } from '../context/AuthContext';
import { useLang } from '../context/LangContext';
import { useEditor } from '../context/EditorContext';
import { toast } from '../hooks/use-toast';
import VideoStats from './VideoStats';

const MyVideos = () => {
  const { user, loading: authLoading, login } = useAuth();
  const { lang } = useLang();
  const { openEditorWithVideo } = useEditor();
  const navigate = useNavigate();
  const [videos, setVideos] = useState([]);
  const [albums, setAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('videos');
  const [filter, setFilter] = useState('all'); // all, favorites
  const [showAlbumBuilder, setShowAlbumBuilder] = useState(false);
  const [albumName, setAlbumName] = useState('');
  const [selectedVideoIds, setSelectedVideoIds] = useState([]);
  const [statsFor, setStatsFor] = useState(null);
  const [albumColor, setAlbumColor] = useState('cyan');
  const albumColors = [
    { key: 'cyan', label: lang === 'sv' ? 'Cyan' : 'سماوي', bg: 'bg-cyan-500' },
    { key: 'pink', label: lang === 'sv' ? 'Rosa' : 'وردي', bg: 'bg-pink-500' },
    { key: 'amber', label: lang === 'sv' ? 'Bärnsten' : 'كهرماني', bg: 'bg-amber-500' },
    { key: 'emerald', label: lang === 'sv' ? 'Smaragd' : 'زمردي', bg: 'bg-emerald-500' },
    { key: 'violet', label: lang === 'sv' ? 'Violett' : 'بنفسجي', bg: 'bg-violet-500' },
    { key: 'slate', label: lang === 'sv' ? 'Skiffer' : 'رمادي', bg: 'bg-slate-600' }
  ];

  const fetchAll = async () => {
    try {
      const [vRes, aRes] = await Promise.all([
        axios.get(`${API}/videos/list`, { withCredentials: true }),
        axios.get(`${API}/albums`, { withCredentials: true })
      ]);
      setVideos(vRes.data.videos || []);
      setAlbums(aRes.data.albums || []);
    } catch (_) {} finally { setLoading(false); }
  };

  useEffect(() => {
    if (!authLoading && user) fetchAll();
    else if (!authLoading) setLoading(false);
  }, [user, authLoading]);

  const remove = async (id) => {
    if (!window.confirm(lang === 'sv' ? 'Ta bort denna video?' : 'حذف هذا الفيديو؟')) return;
    try {
      await axios.delete(`${API}/videos/${id}`, { withCredentials: true });
      setVideos(v => v.filter(x => x.id !== id));
      toast({ title: lang === 'sv' ? 'Video borttagen' : 'تم حذف الفيديو' });
    } catch (_) { toast({ title: 'Kunde inte tas bort' }); }
  };

  const toggleFav = async (v) => {
    try {
      const res = await axios.patch(`${API}/videos/${v.id}/favorite`, {}, { withCredentials: true });
      setVideos(prev => prev.map(x => x.id === v.id ? { ...x, is_favorite: res.data.is_favorite } : x));
    } catch (_) {}
  };

  const reopen = (video) => {
    openEditorWithVideo && openEditorWithVideo({
      videoUrl: `${API}/files/${video.storage_path}`,
      videoName: video.title,
      settings: video.settings || null
    });
    navigate('/');
  };

  const shareVideo = async (video) => {
    const url = `${window.location.origin}/v/${video.id}`;
    try { await navigator.clipboard.writeText(url); toast({ title: lang === 'sv' ? 'Länk kopierad!' : 'تم النسخ!' }); } catch(_){}
  };

  const shareAlbum = async (album) => {
    const url = `${window.location.origin}/a/${album.id}`;
    try { await navigator.clipboard.writeText(url); toast({ title: lang === 'sv' ? 'Album-länk kopierad!' : 'تم نسخ رابط الألبوم!' }); } catch(_){}
  };

  const toggleVideoInAlbum = (id) => {
    setSelectedVideoIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const createAlbum = async () => {
    if (!albumName.trim() || selectedVideoIds.length === 0) {
      toast({ title: lang === 'sv' ? 'Ge albumet ett namn och välj minst en video' : 'اسم وفيديو واحد على الأقل' });
      return;
    }
    try {
      const res = await axios.post(`${API}/albums`, {
        name: albumName.trim(), video_ids: selectedVideoIds, is_public: true, color: albumColor
      }, { withCredentials: true });
      setAlbums(prev => [res.data, ...prev]);
      setAlbumName(''); setSelectedVideoIds([]); setAlbumColor('cyan'); setShowAlbumBuilder(false); setTab('albums');
      toast({ title: lang === 'sv' ? 'Album skapat!' : 'تم إنشاء الألبوم!' });
    } catch (_) { toast({ title: 'Kunde inte skapa album' }); }
  };

  const removeAlbum = async (id) => {
    if (!window.confirm(lang === 'sv' ? 'Ta bort detta album?' : 'حذف الألبوم؟')) return;
    try {
      await axios.delete(`${API}/albums/${id}`, { withCredentials: true });
      setAlbums(prev => prev.filter(a => a.id !== id));
    } catch (_) {}
  };

  if (authLoading) return <div className="min-h-screen flex items-center justify-center bg-white"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;

  if (!user) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gradient-to-b from-white via-cyan-50/40 to-white p-6">
        <div className="max-w-md text-center">
          <div className="w-16 h-16 mx-auto rounded-2xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center mb-6">
            <FolderOpen className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl font-black tracking-tight text-neutral-900 mb-3">{lang === 'sv' ? 'Logga in för att se dina videor' : 'سجّل الدخول لعرض فيديوهاتك'}</h1>
          <p className="text-[15px] text-neutral-600 mb-8">{lang === 'sv' ? 'Alla dina exporterade videor sparas i molnet så du kan komma åt dem från vilken enhet som helst.' : 'يتم حفظ جميع فيديوهاتك المصدرة في السحابة.'}</p>
          <Button onClick={login} className="h-12 px-6 rounded-full bg-black hover:bg-neutral-800 text-white font-bold">
            <LogIn className="w-4 h-4 mr-2 rtl:mr-0 rtl:ml-2" /> {lang === 'sv' ? 'Logga in med Google' : 'الدخول بحساب جوجل'}
          </Button>
          <div className="mt-4"><a href="/" className="text-[13px] text-neutral-500 hover:text-neutral-900 font-medium">← {lang === 'sv' ? 'Tillbaka' : 'رجوع'}</a></div>
        </div>
      </div>
    );
  }

  const shown = filter === 'favorites' ? videos.filter(v => v.is_favorite) : videos;

  return (
    <div className="min-h-screen bg-neutral-50">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-10">
        <div className="flex items-center justify-between mb-8">
          <a href="/" className="inline-flex items-center gap-2 text-neutral-500 hover:text-neutral-900 text-[13.5px] font-semibold">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {lang === 'sv' ? 'Startsidan' : 'الرئيسية'}
          </a>
          <div className="flex items-center gap-2">
            <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-cyan-400 to-fuchsia-500 flex items-center justify-center"><Sparkles className="w-4 h-4 text-white" /></div>
            <span className="font-black text-neutral-900 text-[16px]">CapCute</span>
          </div>
        </div>

        <div className="mb-8">
          <span className="inline-block text-[13px] font-bold tracking-widest uppercase text-cyan-600 mb-3">{lang === 'sv' ? 'Molnbibliotek' : 'المكتبة السحابية'}</span>
          <h1 className="text-4xl md:text-5xl font-black tracking-[-0.02em] text-neutral-900">{lang === 'sv' ? 'Mina videor' : 'فيديوهاتي'}</h1>
        </div>

        <div className="flex items-center gap-2 flex-wrap mb-6">
          <button onClick={() => setTab('videos')} className={`h-10 px-4 rounded-full text-[13.5px] font-bold transition-colors ${tab==='videos' ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-800 border border-neutral-200'}`}>
            {lang === 'sv' ? 'Videor' : 'الفيديوهات'} ({videos.length})
          </button>
          <button onClick={() => setTab('albums')} className={`h-10 px-4 rounded-full text-[13.5px] font-bold transition-colors ${tab==='albums' ? 'bg-neutral-900 text-white' : 'bg-white text-neutral-800 border border-neutral-200'}`}>
            <ListMusic className="w-3.5 h-3.5 inline mr-1.5 rtl:mr-0 rtl:ml-1.5" /> {lang === 'sv' ? 'Album' : 'الألبومات'} ({albums.length})
          </button>
          <div className="flex-1" />
          {tab === 'videos' && (
            <>
              <button onClick={() => setFilter(filter === 'all' ? 'favorites' : 'all')} className={`h-10 px-4 rounded-full text-[13.5px] font-bold transition-colors ${filter === 'favorites' ? 'bg-amber-400 text-black' : 'bg-white text-neutral-800 border border-neutral-200'}`}>
                <Star className={`w-3.5 h-3.5 inline mr-1.5 rtl:mr-0 rtl:ml-1.5 ${filter === 'favorites' ? 'fill-black' : ''}`} />
                {lang === 'sv' ? 'Favoriter' : 'المفضلة'}
              </button>
              <button onClick={() => setShowAlbumBuilder(v => !v)} className="h-10 px-4 rounded-full text-[13.5px] font-bold bg-cyan-500 hover:bg-cyan-600 text-white transition-colors">
                <Plus className="w-3.5 h-3.5 inline mr-1.5 rtl:mr-0 rtl:ml-1.5" /> {lang === 'sv' ? 'Nytt album' : 'ألبوم جديد'}
              </button>
            </>
          )}
        </div>

        {tab === 'videos' && showAlbumBuilder && (
          <div className="mb-6 p-5 rounded-2xl bg-white border border-cyan-300 shadow-sm">
            <div className="flex items-center gap-2 mb-3">
              <ListMusic className="w-4 h-4 text-cyan-600" />
              <h3 className="font-bold text-neutral-900">{lang === 'sv' ? 'Skapa album' : 'إنشاء ألبوم'}</h3>
            </div>
            <Input value={albumName} onChange={(e) => setAlbumName(e.target.value)} placeholder={lang === 'sv' ? 'Albumets namn' : 'اسم الألبوم'} className="h-11 mb-3" />
            <div className="mb-3">
              <label className="text-[11px] uppercase tracking-wider text-neutral-500 font-bold">{lang === 'sv' ? 'Accentfärg' : 'لون الطابع'}</label>
              <div className="mt-2 flex gap-2 flex-wrap">
                {albumColors.map(c => (
                  <button key={c.key} onClick={() => setAlbumColor(c.key)} className={`h-9 pl-1 pr-3 rtl:pl-3 rtl:pr-1 rounded-full flex items-center gap-2 border-2 transition-all ${albumColor === c.key ? 'border-neutral-900 bg-neutral-50' : 'border-transparent bg-neutral-50 hover:bg-neutral-100'}`}>
                    <span className={`w-6 h-6 rounded-full ${c.bg}`} />
                    <span className="text-[12px] font-bold">{c.label}</span>
                  </button>
                ))}
              </div>
            </div>
            <p className="text-[12.5px] text-neutral-500 mb-3">{lang === 'sv' ? 'Välj videor att inkludera:' : 'اختر فيديوهات:'}</p>
            <div className="flex items-center gap-2 mb-4">
              <span className="text-[12px] font-bold text-neutral-700">{selectedVideoIds.length} {lang === 'sv' ? 'valda' : 'محددة'}</span>
              <div className="flex-1" />
              <button onClick={createAlbum} disabled={!albumName.trim() || selectedVideoIds.length === 0} className="h-9 px-4 rounded-full bg-black text-white text-[12.5px] font-bold disabled:opacity-40">
                <Check className="w-3.5 h-3.5 inline mr-1.5" /> {lang === 'sv' ? 'Skapa' : 'إنشاء'}
              </button>
              <button onClick={() => { setShowAlbumBuilder(false); setSelectedVideoIds([]); setAlbumName(''); setAlbumColor('cyan'); }} className="h-9 w-9 rounded-full hover:bg-neutral-100 flex items-center justify-center">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>
        ) : tab === 'videos' ? (
          shown.length === 0 ? (
            <EmptyState lang={lang} type="videos" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {shown.map(v => (
                <div key={v.id} className={`group bg-white rounded-2xl overflow-hidden border transition-all duration-300 ${showAlbumBuilder && selectedVideoIds.includes(v.id) ? 'border-cyan-400 ring-2 ring-cyan-200' : 'border-neutral-200 hover:shadow-xl hover:-translate-y-1'}`}>
                  <div className="relative aspect-video bg-neutral-900 overflow-hidden cursor-pointer" onClick={() => showAlbumBuilder ? toggleVideoInAlbum(v.id) : null}>
                    {v.thumb_path ? (
                      <img src={`${API}/files/${v.thumb_path}`} alt={v.title} className="w-full h-full object-cover" />
                    ) : (<div className="w-full h-full bg-gradient-to-br from-cyan-500 to-fuchsia-500" />)}
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                    {!showAlbumBuilder ? (
                      <a href={`/v/${v.id}`} className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                        <div className="w-14 h-14 rounded-full bg-white/95 flex items-center justify-center shadow-xl"><Play className="w-6 h-6 fill-black text-black ml-0.5" /></div>
                      </a>
                    ) : (
                      <div className={`absolute inset-0 flex items-center justify-center ${selectedVideoIds.includes(v.id) ? 'bg-cyan-500/40' : 'bg-transparent group-hover:bg-black/30'}`}>
                        <div className={`w-11 h-11 rounded-full border-2 flex items-center justify-center transition-all ${selectedVideoIds.includes(v.id) ? 'bg-cyan-500 border-white text-white' : 'bg-white/20 border-white/70 text-white'}`}>
                          {selectedVideoIds.includes(v.id) ? <Check className="w-5 h-5" strokeWidth={3} /> : <Plus className="w-5 h-5" />}
                        </div>
                      </div>
                    )}
                    <button onClick={(e) => { e.stopPropagation(); toggleFav(v); }} className={`absolute top-2 left-2 rtl:left-auto rtl:right-2 w-8 h-8 rounded-full flex items-center justify-center transition-colors ${v.is_favorite ? 'bg-amber-400 text-black' : 'bg-black/50 backdrop-blur text-white hover:bg-black/70'}`}>
                      <Star className={`w-4 h-4 ${v.is_favorite ? 'fill-black' : ''}`} />
                    </button>
                    <div className="absolute top-2 right-2 rtl:right-auto rtl:left-2 flex items-center gap-1 bg-black/60 backdrop-blur text-white text-[10.5px] font-bold rounded-full px-2 py-0.5">
                      <Eye className="w-3 h-3" /> {v.view_count || 0}
                    </div>
                    {v.duration ? (
                      <div className="absolute bottom-2 right-2 rtl:right-auto rtl:left-2 bg-black/70 text-white text-[10.5px] font-bold rounded px-1.5 py-0.5">{Math.round(v.duration)}s</div>
                    ) : null}
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-[15px] text-neutral-900 truncate mb-1">{v.title}</h3>
                    <p className="text-[11.5px] text-neutral-500">{new Date(v.created_at).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'ar-SA')} · {(v.size / 1024 / 1024).toFixed(1)} MB</p>
                    {!showAlbumBuilder && (
                      <div className="mt-3 grid grid-cols-5 gap-1.5">
                        <button onClick={() => reopen(v)} className="h-9 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white text-[11px] font-bold flex items-center justify-center" title={lang === 'sv' ? 'Redigera igen' : 'إعادة تحرير'}><Edit3 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => setStatsFor(v)} className="h-9 rounded-lg bg-cyan-50 hover:bg-cyan-100 text-cyan-700 text-[11px] font-bold flex items-center justify-center" title={lang === 'sv' ? 'Statistik' : 'إحصائيات'}><TrendingUp className="w-3.5 h-3.5" /></button>
                        <a href={`${API}/files/${v.storage_path}`} download className="h-9 rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-800 text-[11px] font-bold flex items-center justify-center" title={lang === 'sv' ? 'Ladda ner' : 'تنزيل'}><Download className="w-3.5 h-3.5" /></a>
                        <button onClick={() => shareVideo(v)} className="h-9 rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-800 text-[11px] font-bold flex items-center justify-center" title={lang === 'sv' ? 'Dela' : 'مشاركة'}><Share2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => remove(v.id)} className="h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-[11px] font-bold flex items-center justify-center" title={lang === 'sv' ? 'Ta bort' : 'حذف'}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )
        ) : (
          albums.length === 0 ? (
            <EmptyState lang={lang} type="albums" />
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {albums.map(al => {
                const bgMap = { cyan: 'from-cyan-500 to-fuchsia-500', pink: 'from-pink-500 to-rose-500', amber: 'from-amber-500 to-orange-500', emerald: 'from-emerald-500 to-teal-500', violet: 'from-violet-500 to-indigo-500', slate: 'from-slate-600 to-neutral-700' };
                const bgCls = bgMap[al.color] || bgMap.cyan;
                return (
                <div key={al.id} className="group bg-white rounded-2xl overflow-hidden border border-neutral-200 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                  <a href={`/a/${al.id}`} className={`block relative aspect-video bg-gradient-to-br ${bgCls} overflow-hidden`}>
                    <div className="absolute inset-0 grid grid-cols-2 grid-rows-2 gap-1 p-1">
                      {(al.video_ids || []).slice(0, 4).map((vid, i) => {
                        const video = videos.find(x => x.id === vid);
                        return video?.thumb_path ? <img key={i} src={`${API}/files/${video.thumb_path}`} alt="" className="w-full h-full object-cover rounded-md" /> : <div key={i} className="w-full h-full bg-black/20 rounded-md" />;
                      })}
                    </div>
                    <div className="absolute bottom-3 left-3 rtl:left-auto rtl:right-3 bg-black/70 backdrop-blur rounded-full px-2.5 py-1 text-white text-[10.5px] font-bold flex items-center gap-1">
                      <ListMusic className="w-3 h-3" /> {(al.video_ids || []).length} {lang === 'sv' ? 'videor' : 'فيديو'}
                    </div>
                  </a>
                  <div className="p-4">
                    <h3 className="font-bold text-[15px] text-neutral-900 truncate mb-1">{al.name}</h3>
                    <p className="text-[11.5px] text-neutral-500 mb-3">{new Date(al.created_at).toLocaleDateString(lang === 'sv' ? 'sv-SE' : 'ar-SA')}</p>
                    <div className="grid grid-cols-3 gap-1.5">
                      <a href={`/a/${al.id}`} className="h-9 rounded-lg bg-neutral-900 text-white text-[11.5px] font-bold flex items-center justify-center gap-1"><Play className="w-3 h-3 fill-white" /> {lang === 'sv' ? 'Öppna' : 'فتح'}</a>
                      <button onClick={() => shareAlbum(al)} className="h-9 rounded-lg bg-neutral-100 hover:bg-neutral-200 text-neutral-800 text-[11.5px] font-bold flex items-center justify-center"><Share2 className="w-3.5 h-3.5" /></button>
                      <button onClick={() => removeAlbum(al.id)} className="h-9 rounded-lg bg-red-50 hover:bg-red-100 text-red-600 text-[11.5px] font-bold flex items-center justify-center"><Trash2 className="w-3.5 h-3.5" /></button>
                    </div>
                  </div>
                </div>
                );
              })}
            </div>
          )
        )}
      </div>
      {statsFor && <VideoStats videoId={statsFor.id} videoTitle={statsFor.title} onClose={() => setStatsFor(null)} />}
    </div>
  );
};

const EmptyState = ({ lang, type }) => (
  <div className="bg-white rounded-3xl border border-neutral-200 p-14 text-center">
    {type === 'videos' ? <FolderOpen className="w-14 h-14 mx-auto text-neutral-300 mb-4" /> : <ListMusic className="w-14 h-14 mx-auto text-neutral-300 mb-4" />}
    <h3 className="text-xl font-bold text-neutral-900 mb-2">{type === 'videos' ? (lang === 'sv' ? 'Inga videor ännu' : 'لا توجد فيديوهات') : (lang === 'sv' ? 'Inga album ännu' : 'لا توجد ألبومات')}</h3>
    <p className="text-[14px] text-neutral-500">{type === 'videos' ? (lang === 'sv' ? 'Exporterade videor visas här automatiskt.' : 'ستظهر الفيديوهات المصدرة هنا.') : (lang === 'sv' ? 'Skapa ett album från fliken Videor.' : 'أنشئ ألبوماً من علامة الفيديوهات.')}</p>
  </div>
);

export default MyVideos;
